/**
 * Forums Grid Block — Editor
 *
 * @package BBP_Core
 */
import { __ } from '@wordpress/i18n';
import {
	useBlockProps,
	InspectorControls,
} from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	TextControl,
	ToggleControl,
	RangeControl,
	ColorPalette,
	Placeholder,
	Spinner,
} from '@wordpress/components';
import ServerSideRender from '@wordpress/server-side-render';
import './editor.scss';

/**
 * Skeleton loader component displayed while the server-side render loads.
 *
 * @return {JSX.Element} Skeleton loader markup.
 */
const SkeletonLoader = () => (
	<div className="bbpc-forums-skeleton">
		{ [ 1, 2, 3 ].map( ( i ) => (
			<div key={ i } className="bbpc-forums-skeleton__card">
				<div className="bbpc-forums-skeleton__image" />
				<div className="bbpc-forums-skeleton__title" />
				<div className="bbpc-forums-skeleton__meta" />
			</div>
		) ) }
	</div>
);

/**
 * Edit component for the Forums Grid block.
 *
 * @param {Object}   props               Block props.
 * @param {Object}   props.attributes    Block attributes.
 * @param {Function} props.setAttributes Attribute setter.
 * @return {JSX.Element} Block editor output.
 */
export default function Edit( { attributes, setAttributes } ) {
	const blockProps = useBlockProps();

	/* Check if Forumax Pro is active. */
	const isProActive =
		window.bbpc_upsell_config?.is_pro_active === '1' ||
		window.bbpc_upsell_config?.is_pro_active === true;

	const {
		ppp,
		ppp2,
		order,
		orderby,
		forum_filter_ids,
		columns,
		forum_image_size,
		show_forum_image,
		show_post_count,
		show_reply_count,
		show_excerpt,
		is_more_btn,
		more_txt,
		less_txt,
		no_posts_message,
		enable_card_hover,
		title_color,
		title_hover_color,
		content_color,
		more_text_color,
		card_bg_color,
		btn_color,
		btn_bg_color,
		title_font_size,
		meta_font_size,
		card_gap,
		card_border_radius,
		btn_border_radius,
		hide_image_mobile,
		mobile_columns,
	} = attributes;

	const orderByOptions = [
		{ label: __( 'Date', 'bbp-core' ), value: 'date' },
		{ label: __( 'Title', 'bbp-core' ), value: 'title' },
		{ label: __( 'Modified', 'bbp-core' ), value: 'modified' },
		{ label: __( 'Menu Order', 'bbp-core' ), value: 'menu_order' },
		{ label: __( 'ID', 'bbp-core' ), value: 'ID' },
	];

	const defaultColors = [
		{ name: __( 'Blue', 'bbp-core' ), color: '#3b82f6' },
		{ name: __( 'Green', 'bbp-core' ), color: '#10b981' },
		{ name: __( 'Red', 'bbp-core' ), color: '#ef4444' },
		{ name: __( 'Purple', 'bbp-core' ), color: '#8b5cf6' },
		{ name: __( 'Orange', 'bbp-core' ), color: '#f59e0b' },
		{ name: __( 'Gray', 'bbp-core' ), color: '#6b7280' },
		{ name: __( 'White', 'bbp-core' ), color: '#ffffff' },
		{ name: __( 'Black', 'bbp-core' ), color: '#000000' },
	];

	return (
		<div { ...blockProps }>
			{ /* ======== SETTINGS TAB ======== */ }
			<InspectorControls>
				{ /* -------- Filter Options -------- */ }
				<PanelBody title={ __( 'Filter Options', 'bbp-core' ) }>
					<TextControl
						label={ __( 'Show Forums', 'bbp-core' ) }
						type="number"
						value={ ppp }
						onChange={ ( value ) =>
							setAttributes( { ppp: parseInt( value ) } )
						}
						help={ __( 'Forums count at initial view.', 'bbp-core' ) }
					/>
					<TextControl
						label={ __( 'Hidden Forums', 'bbp-core' ) }
						type="number"
						value={ ppp2 }
						onChange={ ( value ) =>
							setAttributes( { ppp2: parseInt( value ) } )
						}
						help={ __(
							'Additional forums shown on clicking "More" button.',
							'bbp-core'
						) }
					/>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<RangeControl
							label={ __( 'Grid Columns', 'bbp-core' ) }
							value={ isProActive ? columns : 3 }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { columns: value } );
								}
							} }
							min={ 1 }
							max={ 6 }
							disabled={ ! isProActive }
						/>
					</div>
					<SelectControl
						label={ __( 'Order', 'bbp-core' ) }
						value={ order }
						options={ [
							{ label: 'ASC', value: 'ASC' },
							{ label: 'DESC', value: 'DESC' },
						] }
						onChange={ ( value ) =>
							setAttributes( { order: value } )
						}
					/>
					<SelectControl
						label={ __( 'Order By', 'bbp-core' ) }
						value={ orderby }
						options={ orderByOptions }
						onChange={ ( value ) =>
							setAttributes( { orderby: value } )
						}
					/>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<TextControl
							label={ __( 'Filter by Forum IDs', 'bbp-core' ) }
							value={ isProActive ? forum_filter_ids : '' }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { forum_filter_ids: value } );
								}
							} }
							help={ __(
								'Comma-separated forum IDs. Leave empty to show all.',
								'bbp-core'
							) }
							disabled={ ! isProActive }
						/>
					</div>
					<SelectControl
						label={ __( 'Forum Image Size', 'bbp-core' ) }
						value={ forum_image_size }
						options={ [
							{
								label: __( 'Thumbnail', 'bbp-core' ),
								value: 'thumbnail',
							},
							{
								label: __( 'Medium', 'bbp-core' ),
								value: 'medium',
							},
							{
								label: __( 'Large', 'bbp-core' ),
								value: 'large',
							},
							{
								label: __( 'Full', 'bbp-core' ),
								value: 'full',
							},
						] }
						onChange={ ( value ) =>
							setAttributes( { forum_image_size: value } )
						}
					/>
				</PanelBody>

				{ /* -------- "More" Button -------- */ }
				<PanelBody
					title={ __( 'More Button', 'bbp-core' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __( 'Show "More" Button', 'bbp-core' ) }
						checked={ is_more_btn }
						onChange={ ( value ) =>
							setAttributes( { is_more_btn: value } )
						}
					/>
					{ is_more_btn && (
						<>
							<TextControl
								label={ __( 'More Text', 'bbp-core' ) }
								value={ more_txt }
								onChange={ ( value ) =>
									setAttributes( { more_txt: value } )
								}
							/>
							<TextControl
								label={ __( 'Less Text', 'bbp-core' ) }
								value={ less_txt }
								onChange={ ( value ) =>
									setAttributes( { less_txt: value } )
								}
							/>
						</>
					) }
				</PanelBody>

				{ /* -------- Visibility -------- */ }
				<PanelBody
					title={ __( 'Visibility', 'bbp-core' ) }
					initialOpen={ false }
				>
					<ToggleControl
						label={ __( 'Show Forum Image', 'bbp-core' ) }
						checked={ show_forum_image }
						onChange={ ( value ) =>
							setAttributes( { show_forum_image: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Post Count', 'bbp-core' ) }
						checked={ show_post_count }
						onChange={ ( value ) =>
							setAttributes( { show_post_count: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Reply Count', 'bbp-core' ) }
						checked={ show_reply_count }
						onChange={ ( value ) =>
							setAttributes( { show_reply_count: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Show Forum Excerpt', 'bbp-core' ) }
						checked={ show_excerpt }
						onChange={ ( value ) =>
							setAttributes( { show_excerpt: value } )
						}
					/>
					<ToggleControl
						label={ __( 'Enable Card Hover Animation', 'bbp-core' ) }
						checked={ enable_card_hover }
						onChange={ ( value ) =>
							setAttributes( { enable_card_hover: value } )
						}
					/>
				</PanelBody>

				{ /* -------- General -------- */ }
				<PanelBody
					title={ __( 'General', 'bbp-core' ) }
					initialOpen={ false }
				>
					<TextControl
						label={ __(
							'"No Forums Found" Message',
							'bbp-core'
						) }
						value={ no_posts_message }
						onChange={ ( value ) =>
							setAttributes( { no_posts_message: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Responsive -------- */ }
				<PanelBody
					title={ __( 'Responsive', 'bbp-core' ) }
					initialOpen={ false }
				>
					<div className={ ! isProActive ? 'bbpc-pro-gated' : '' }>
						{ ! isProActive && (
							<span className="bbpc-pro-watermark">PRO</span>
						) }
						<p className="components-base-control__help">
							{ __(
								'Controls for mobile devices (< 768px).',
								'bbp-core'
							) }
						</p>
						<RangeControl
							label={ __( 'Mobile Columns', 'bbp-core' ) }
							value={ isProActive ? mobile_columns : 1 }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { mobile_columns: value } );
								}
							} }
							min={ 1 }
							max={ 3 }
							disabled={ ! isProActive }
						/>
						<ToggleControl
							label={ __( 'Hide Forum Image on Mobile', 'bbp-core' ) }
							checked={ isProActive ? hide_image_mobile : false }
							onChange={ ( value ) => {
								if ( isProActive ) {
									setAttributes( { hide_image_mobile: value } );
								}
							} }
							disabled={ ! isProActive }
						/>
					</div>
				</PanelBody>
			</InspectorControls>

			{ /* ======== STYLES TAB ======== */ }
			<InspectorControls group="styles">
				{ /* -------- Colors -------- */ }
				<PanelBody
					title={ __( 'Colors', 'bbp-core' ) }
					initialOpen={ false }
				>
					<p className="bbpc-color-label">
						{ __( 'Title Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ title_color }
						onChange={ ( value ) =>
							setAttributes( { title_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Title Hover Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ title_hover_color }
						onChange={ ( value ) =>
							setAttributes( { title_hover_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Content Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ content_color }
						onChange={ ( value ) =>
							setAttributes( { content_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Card Background Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ card_bg_color }
						onChange={ ( value ) =>
							setAttributes( { card_bg_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'More Text Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ more_text_color }
						onChange={ ( value ) =>
							setAttributes( { more_text_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Button Color', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ btn_color }
						onChange={ ( value ) =>
							setAttributes( { btn_color: value } )
						}
					/>

					<p className="bbpc-color-label">
						{ __( 'Button Background', 'bbp-core' ) }
					</p>
					<ColorPalette
						colors={ defaultColors }
						value={ btn_bg_color }
						onChange={ ( value ) =>
							setAttributes( { btn_bg_color: value } )
						}
					/>
				</PanelBody>

				{ /* -------- Typography -------- */ }
				<PanelBody
					title={ __( 'Typography', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Title Font Size (px)', 'bbp-core' ) }
						value={ title_font_size }
						onChange={ ( value ) =>
							setAttributes( { title_font_size: value } )
						}
						min={ 0 }
						max={ 48 }
					/>
					<RangeControl
						label={ __( 'Meta Font Size (px)', 'bbp-core' ) }
						value={ meta_font_size }
						onChange={ ( value ) =>
							setAttributes( { meta_font_size: value } )
						}
						min={ 0 }
						max={ 32 }
					/>
				</PanelBody>

				{ /* -------- Spacing -------- */ }
				<PanelBody
					title={ __( 'Spacing', 'bbp-core' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Card Gap (px)', 'bbp-core' ) }
						value={ card_gap }
						onChange={ ( value ) =>
							setAttributes( { card_gap: value } )
						}
						min={ 0 }
						max={ 60 }
					/>
					<RangeControl
						label={ __(
							'Card Border Radius (px)',
							'bbp-core'
						) }
						value={ card_border_radius }
						onChange={ ( value ) =>
							setAttributes( {
								card_border_radius: value,
							} )
						}
						min={ 0 }
						max={ 30 }
					/>
					<RangeControl
						label={ __(
							'Button Border Radius (px)',
							'bbp-core'
						) }
						value={ btn_border_radius }
						onChange={ ( value ) =>
							setAttributes( {
								btn_border_radius: value,
							} )
						}
						min={ 0 }
						max={ 30 }
					/>
				</PanelBody>
			</InspectorControls>

			{ /* ======== BLOCK PREVIEW ======== */ }
			<ServerSideRender
				block="bbp-core/forums"
				attributes={ attributes }
				LoadingResponsePlaceholder={ SkeletonLoader }
				ErrorResponsePlaceholder={ () => (
					<Placeholder
						icon="warning"
						label={ __( 'BBPC Forums Grid', 'bbp-core' ) }
					>
						{ __( 'Error loading block preview.', 'bbp-core' ) }
					</Placeholder>
				) }
			/>
		</div>
	);
}
